<template>
  <div class="px-14 w-full h-full bg-white">
    <app-header class="w-full tools-header">
      <div class="flex items-center justify-between w-full gap-4 py-8">
        <div class="flex flex-col gap-1 w-7/12">
          <h2 class="font-semibold">Tools</h2>
          <span>
            Tools let the AI look things up while it answers — an order status, stock, a booking.
            Each one wraps an API call with a description and typed parameters. Tools are shared between all your bots.
          </span>
        </div>

        <div class="w-4/12">
          <search-input v-model="searchQuery" :is-disabled="isLoading" variant="search-variables"/>
        </div>

        <busy-button id="tool-add-button"
                     data-testid="tool-add"
                     :is-loading="isLoading"
                     icon="add"
                     icon-size="1.5rem"
                     variant="primary"
                     @click="startCreate"
        >
          <span class="my-auto ml-1">Add</span>
        </busy-button>
      </div>
    </app-header>

    <with-loader id="tools-table"
                 :loading="isLoading"
                 class="mt-8 rounded-lg outline-soft border-soft"
                 use-as-overlay
    >
      <!-- The rows are flex, so table-fixed is what keeps the table at 100%: without it it grows to the
           widest URL and pushes the actions column off-screen instead of truncating (PRO-613). -->
      <table class="w-full table-fixed rounded-l border-collapse">
        <thead class="bg-gray text-white rounded-lg">
          <tr class="flex w-full py-2">
            <th class="w-3/12 text-base font-normal text-left pl-7">Tool</th>
            <th class="w-4/12 text-base font-normal text-left">What it does</th>
            <th class="w-4/12 text-base font-normal text-left">Request</th>
            <th class="w-44 flex-shrink-0 text-base font-normal"/>
          </tr>
        </thead>
        <tbody v-bar>
          <div class="divide-x-0 divide-y divide-solid divide-soft max-h-75vh">
            <tr v-for="tool in visibleTools"
                :key="tool.id"
                class="flex w-full py-3 items-center hover:bg-soft cursor-pointer"
                :data-testid="`tool-row-${tool.name}`"
                @click="startEdit(tool)"
            >
              <td class="w-3/12 min-w-0 flex items-center pl-7 pr-3">
                <span class="font-mono text-base text-gray-dark truncate">{{ tool.name }}</span>
              </td>
              <td class="w-4/12 min-w-0 flex items-center pr-3">
                <span class="text-base text-gray-dark truncate">{{ tool.description }}</span>
              </td>
              <td class="w-4/12 flex items-baseline pr-3 min-w-0">
                <span class="text-sm font-semibold mr-2 flex-shrink-0" :class="httpVerbClass(tool.http_verb)">
                  {{ tool.http_verb }}
                </span>
                <span class="text-sm text-gray truncate">{{ tool.url }}</span>
              </td>
              <td class="w-44 flex-shrink-0 flex items-center justify-end pr-3" @click.stop>
                <div class="flex items-center gap-2">
                  <vx-tooltip v-for="item in menuItems(tool)"
                              :key="item.label"
                              :class="item.classes"
                              :text="item.label"
                              class="flex items-center justify-center h-12 w-12 rounded-full cursor-pointer z-10"
                              position="bottom"
                              @click.native="item.function"
                  >
                    <component :is="item.component" class="h-5 w-5"/>
                  </vx-tooltip>
                </div>
              </td>
            </tr>

            <tr v-if="!visibleTools.length" class="flex w-full">
              <td class="w-full flex flex-col items-center py-12 text-gray">
                <span class="text-lg">{{ tools.length ? 'No tools match your search.' : 'No tools yet.' }}</span>
                <span v-if="!tools.length" class="mt-1 text-base">
                  Add one to let the AI look up live data while it answers.
                </span>
              </td>
            </tr>
          </div>
        </tbody>
      </table>
    </with-loader>

    <drawer-card v-if="editedTool" v-model="showEditor" drawer-width="55%" fullscreen-overlay>
      <template #drawer-content>
        <tool-editor :key="editedTool.id || 'new'"
                     :tool="editedTool"
                     @saved="closeEditor"
                     @cancel="closeEditor"
        />
      </template>
    </drawer-card>

    <drawer-card v-if="usagesToolId" v-model="showUsages" drawer-width="40%" fullscreen-overlay>
      <template #drawer-content>
        <tool-usages :tool-id="usagesToolId" @show-item="showUsages = false"/>
      </template>
    </drawer-card>
  </div>
</template>

<script>
import sanitizeString from 'replace-special-characters'
import AppHeader from '@/components/common/AppHeader.vue'
import BusyButton from '@/components/common/BusyButton.vue'
import DrawerCard from '@/components/base-components/DrawerCard.vue'
import SearchInput from '@/components/base-components/SearchInput.vue'
import ToolEditor from '@/components/tools/ToolEditor.vue'
import ToolUsages from '@/components/tools/ToolUsages.vue'
import WithLoader from '@/components/common/WithLoader.vue'
import DeleteIcon from '@/assets/images/icons/DeleteIcon.vue'
import EditIcon from '@/assets/images/icons/EditIcon.vue'
import EyeIcon from '@/assets/images/icons/EyeIcon.vue'
import toolsModule from '@/store/modules/tools.module'

// Same colour language the API call editor uses for verbs, so a tool reads like an API call.
const HTTP_VERB_CLASSES = {
  GET: 'text-primary',
  POST: 'text-focus-success',
  PUT: 'text-focus-warning',
  PATCH: 'text-focus-warning',
  DELETE: 'text-focus-error'
}

export default {
  name: 'Tools',

  components: {
    AppHeader, BusyButton, DrawerCard, SearchInput, ToolEditor, ToolUsages, WithLoader,
    DeleteIcon, EditIcon, EyeIcon
  },

  data() {
    return {
      searchQuery: '',
      // the tool open in the editor drawer; a create is an object with no id
      editedTool: null,
      showEditor: false,
      usagesToolId: null,
      showUsages: false
    }
  },

  computed: {
    tools: toolsModule.tools,
    isLoading: toolsModule.toolsLoading,

    visibleTools() {
      if (!this.searchQuery) return this.tools

      const term = sanitizeString(this.searchQuery.toLowerCase().trim())
      return this.tools.filter(tool =>
        sanitizeString(`${tool.name}${tool.description}${tool.url}`.toLowerCase()).includes(term)
      )
    }
  },

  watch: {
    // DrawerCard animates itself out, so the tool is cleared only once it is closed — clearing it
    // immediately would blank the drawer's content mid-animation.
    showEditor(isOpen) {
      if (!isOpen) this.editedTool = null
    },
    showUsages(isOpen) {
      if (!isOpen) this.usagesToolId = null
    }
  },

  methods: {
    httpVerbClass(verb) {
      return HTTP_VERB_CLASSES[verb] || 'text-gray-dark'
    },

    menuItems(tool) {
      return [
        {
          label: 'Edit tool',
          component: 'edit-icon',
          classes: 'text-gray-dark bg-soft',
          function: () => this.startEdit(tool)
        },
        {
          label: 'Where this tool is used',
          component: 'eye-icon',
          classes: 'text-gray-dark bg-soft',
          function: () => this.viewUsages(tool.id)
        },
        {
          label: 'Delete tool',
          component: 'delete-icon',
          classes: 'text-focus-error bg-focus-error-light',
          function: () => this.remove(tool)
        }
      ]
    },

    startCreate() {
      this.editedTool = {}
      this.showEditor = true
    },

    startEdit(tool) {
      this.editedTool = tool
      this.showEditor = true
    },

    closeEditor() {
      this.showEditor = false
    },

    viewUsages(toolId) {
      this.usagesToolId = toolId
      this.showUsages = true
    },

    async remove(tool) {
      const { deleted, status } = await toolsModule.deleteTool(tool.id)
      // The engine refuses with 409 while an answer, event or block still references the tool. The
      // toast says so; opening the usages drawer shows WHICH ones, so the message is actionable. Any
      // other failure (timeout, 403, ...) already has its own toast and has nothing to do with usages.
      if (!deleted && status === 409) this.viewUsages(tool.id)
    }
  },

  async mounted() {
    await toolsModule.fetchTools()
  }
}
</script>

<style lang="scss">
.tools-header {
  height: unset !important;
}

#tool-add-button {
  button {
    @apply flex flex-row-reverse;
  }
}
</style>
